import React from "react";
import { List } from "react-window";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Loader2 } from "lucide-react";
import ExecutionCard from "./ExecutionCard";
import { COLUMN_STYLES, COUNT_STYLES } from "./kanbanStyles";

const ROW_HEIGHT = 76, GAP = 6, PAGE_SIZE = 25;

const CardRow = ({ index, style, executions, onViewDetails, onLoadMore, total }) => {
    if (index >= executions.length) {
        return <div style={style} className="flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
    }
    if (index === executions.length - 5 && executions.length < total) onLoadMore();
    return (
        <div style={{ ...style, height: style.height - GAP, paddingLeft: 8, paddingRight: 8 }}>
            <ExecutionCard execution={executions[index]} onViewDetails={onViewDetails} />
        </div>
    );
};

function KanbanColumn({ columnKey, titleKey, statuses, filters, onViewDetails, getEffectiveStatus }) {
    const { t } = useTranslation();
    const containerRef = React.useRef(null);
    const [height, setHeight] = React.useState(0);
    const [executions, setExecutions] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(1);
    const loadingRef = React.useRef(false);
    const { call, isLoading } = useBluelagoon({ method: "getAllWorkflowExecutions" });

    const fetchPage = React.useCallback(async (pageNum) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        const res = await call({ page: pageNum, limit: PAGE_SIZE, status: statuses[0], ...filters });
        if (res?.data) {
            const mapped = (res.data.executions || []).map((e) => ({ ...e, _effective: getEffectiveStatus(e) }));
            setExecutions((prev) => pageNum === 1 ? mapped : [...prev, ...mapped]);
            setTotal(res.data.pagination?.total || 0);
        }
        loadingRef.current = false;
    }, [statuses, filters, getEffectiveStatus]);

    React.useEffect(() => { setPage(1); setExecutions([]); fetchPage(1); }, [fetchPage]);

    const loadMore = React.useCallback(() => {
        if (loadingRef.current || executions.length >= total) return;
        const next = page + 1;
        setPage(next);
        fetchPage(next);
    }, [page, executions.length, total, fetchPage]);

    React.useEffect(() => {
        if (!containerRef.current) return;
        setHeight(containerRef.current.getBoundingClientRect().height);
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) setHeight(entry.contentRect.height);
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const rowCount = executions.length < total ? executions.length + 1 : executions.length;

    return (
        <div className={`flex flex-col min-w-0 min-h-0 bg-muted/20 rounded border-t-2 overflow-hidden ${COLUMN_STYLES[columnKey] || ""}`}>
            <div className="flex items-center justify-between px-3 py-2 shrink-0">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t(titleKey)}</h3>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${COUNT_STYLES[columnKey] || "bg-gray-100 text-gray-600"}`}>{total}</span>
            </div>
            <div ref={containerRef} className="flex-1 min-h-0 overflow-hidden">
                {executions.length === 0 && !isLoading ? (
                    <p className="text-[10px] text-muted-foreground text-center py-4">{t("monitoring.kanban.empty")}</p>
                ) : executions.length === 0 && isLoading ? (
                    <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                ) : height > 0 ? (
                    <List height={height} rowCount={rowCount} rowHeight={ROW_HEIGHT + GAP} rowComponent={CardRow} rowProps={{ executions, onViewDetails, onLoadMore: loadMore, total }} width="100%" style={{ overflowY: "auto" }} />
                ) : null}
            </div>
        </div>
    );
}

export default KanbanColumn;
